import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "feature" | "compact" | "hero";
  index?: number;
}

const categoryPath = (slug: string) => `/topic/${slug}`;

export default function ArticleCard({
  article,
  variant = "default",
  index,
}: ArticleCardProps) {
  const meta = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-foreground-500">
      <Link
        to={categoryPath(article.categorySlug)}
        className="rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
      >
        {article.category}
      </Link>
      <span className="whitespace-nowrap">{article.date}</span>
      <span className="whitespace-nowrap">{article.readTime}</span>
    </div>
  );

  if (variant === "hero") {
    return (
      <article className="group grid grid-cols-1 gap-7 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <Link to={categoryPath(article.categorySlug)} className="cursor-pointer">
            <span className="label-caps text-[11px] text-primary-600">
              本期头条 · {article.category}
            </span>
          </Link>
          <h2 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl lg:text-4xl">
            <Link
              to={`/analysis/${article.slug}`}
              className="transition-colors hover:text-primary-700 cursor-pointer"
            >
              {article.title}
            </Link>
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {article.summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{article.author}</span>
            <span className="whitespace-nowrap">{article.date}</span>
            <span className="whitespace-nowrap">{article.readTime}</span>
            <span className="flex items-center gap-1 whitespace-nowrap text-accent-700">
              <i className="ri-fire-line" aria-hidden="true" />
              热度 {article.heat}
            </span>
          </div>
          <Link
            to={`/analysis/${article.slug}`}
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            阅读完整解读
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
        <div className="lg:col-span-5">
          <Link
            to={`/analysis/${article.slug}`}
            className="block h-56 w-full overflow-hidden rounded-lg border border-background-200 sm:h-72 lg:h-80 cursor-pointer"
          >
            <img
              src={article.cover}
              alt={`${article.title} - ${article.category}事件解读配图`}
              title={`${article.title} - 15黑料网事件深度解读`}
              className="h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </Link>
        </div>
      </article>
    );
  }

  if (variant === "feature") {
    return (
      <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <Link to={`/analysis/${article.slug}`} className="block cursor-pointer">
          <div className="h-48 w-full overflow-hidden bg-background-100">
            <img
              src={article.cover}
              alt={`${article.title} - ${article.category}事件深度解读配图`}
              title={`${article.title} - 15黑料网事件分析`}
              className="h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="flex flex-1 flex-col p-5">
          {meta}
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
            <Link
              to={`/analysis/${article.slug}`}
              className="transition-colors hover:text-primary-700 cursor-pointer"
            >
              {article.title}
            </Link>
          </h3>
          <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <Link
            to={`/analysis/${article.slug}`}
            className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
          >
            查看解读
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article className="group flex gap-4 border-b border-background-200 py-5 last:border-b-0">
        <span className="label-caps w-8 shrink-0 pt-1 font-heading text-lg font-black text-background-400">
          {typeof index === "number" ? String(index + 1).padStart(2, "0") : ""}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
            <Link
              to={`/analysis/${article.slug}`}
              className="transition-colors hover:text-primary-700 cursor-pointer"
            >
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <Link
              to={categoryPath(article.categorySlug)}
              className="whitespace-nowrap text-secondary-700 transition-colors hover:text-primary-600 cursor-pointer"
            >
              {article.category}
            </Link>
            <span className="whitespace-nowrap">{article.date}</span>
            <span className="flex items-center gap-1 whitespace-nowrap text-accent-700">
              <i className="ri-fire-line" aria-hidden="true" />
              {article.heat}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col border-b border-background-200 pb-6">
      <Link to={`/analysis/${article.slug}`} className="cursor-pointer">
        <span className="label-caps text-[11px] text-primary-600">{article.category}</span>
        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <span className="transition-colors group-hover:text-primary-700">{article.title}</span>
        </h3>
      </Link>
      <p className="mt-3 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
        {article.summary}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
        <span className="whitespace-nowrap">{article.date}</span>
        <span className="whitespace-nowrap">{article.readTime}</span>
      </div>
    </article>
  );
}